import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Pagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import PlayersFilters from "src/components/players/PlayersFilters";
import PlayersTable from "src/components/players/PlayersTable";
import EditPlayerModal from "src/components/players/EditPlayerModal";
import { get, post } from "src/utils/networkUtils";

const PAGE_SIZE = 20;

// Helper functions for Azure image URLs
const getFlagUrl = (flagKey) => {
  if (!flagKey) return null;
  return `https://engagecraftfantasydev.blob.core.windows.net/country-flags/${flagKey}.png`;
};

const PLACEHOLDER_JERSEY_URL =
  "https://engagecraftfantasydev.blob.core.windows.net/teams/shirts/placeholder@1x.png";

const getJerseyUrl = (shirtKey, scale = "1x") => {
  if (!shirtKey) return PLACEHOLDER_JERSEY_URL;
  return `https://engagecraftfantasydev.blob.core.windows.net/teams/shirts/${shirtKey}@${scale}.png`;
};

const PlayersPage = () => {
  const inputStyles = {
    borderRadius: "50px",
    boxShadow: "0.5px 3px 10px 0px #7777771A",
    "& .MuiOutlinedInput-root": {
      borderRadius: "50px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "50px",
      borderColor: "#ccc",
    },
    "& .MuiInputBase-input": {
      color: "#6A707E",
      fontSize: "16px",
      fontWeight: 600,
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#6A707E",
    },
    "& .MuiInputLabel-root": {
      color: "#6A707E",
      fontSize: "16px",
      fontWeight: 600,
    },
  };

  const [players, setPlayers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedLeague, setSelectedLeague] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedPosition, setSelectedPosition] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPlayerId, setMenuPlayerId] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const navigate = useNavigate();

  // Debounce search: wait 400ms after typing stops, then trigger fetch from page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handlePositionChange = (value) => {
    setSelectedPosition(value);
    setPage(1);
  };

  const handleSortByName = (direction) => {
    setSortField('name');
    setSortDirection(direction);
    const sorted = [...players].sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return direction === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
    setPlayers(sorted);
  };

  const handleSortByStatus = (direction) => {
    setSortField('status');
    setSortDirection(direction);
    const sorted = [...players].sort((a, b) => {
      const statusA = (a.availabilityDisplay || 'available').toLowerCase();
      const statusB = (b.availabilityDisplay || 'available').toLowerCase();
      return direction === 'asc' ? statusA.localeCompare(statusB) : statusB.localeCompare(statusA);
    });
    setPlayers(sorted);
  };

  const handleFilterByStatus = (status) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        return prev.filter((s) => s !== status);
      } else {
        return [...prev, status];
      }
    });
    setPage(1);
  };

  const handleMenuOpen = (event, playerId) => {
    setAnchorEl(event.currentTarget);
    setMenuPlayerId(playerId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPlayerId(null);
  };

  const handleEdit = (playerId) => {
    navigate(`/dashboard/players/${playerId}`);
    handleMenuClose();
  };

  const handleAdd = () => {
    navigate("/dashboard/players/new");
  };

  const handleDelete = () => {
    handleMenuClose();
  };

  const handleEditPlayer = () => {
    const player = players.find((p) => p.id === menuPlayerId);
    if (player) {
      setSelectedPlayer(player);
      setEditModalOpen(true);
    }
    handleMenuClose();
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
    setSelectedPlayer(null);
  };

  const handlePlayerUpdated = (response) => {
    const player = response?.data || response;
    if (player?.id) {
      setPlayers((prevPlayers) =>
        prevPlayers.map((p) =>
          p.id === player.id
            ? {
                ...p,
                firstName: player.firstName ?? p.firstName,
                lastName: player.lastName ?? p.lastName,
                matchName: player.matchName ?? p.matchName,
                displayName: player.displayName ?? p.displayName,
                name: `${player.firstName ?? p.firstName ?? ""} ${
                  player.lastName ?? p.lastName ?? ""
                }`.trim(),
                price: player.price ?? p.price,
                position: player.position ?? p.position,
                availabilityDisplay:
                  player.availabilityDisplay ?? p.availabilityDisplay,
                isVisible: player.isVisible ?? p.isVisible,
                hasLeft: player.hasLeft ?? p.hasLeft,
              }
            : p
        )
      );
    }
    setSnackbar({
      open: true,
      message: "Player updated successfully",
      severity: "success",
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRecalculateOwnership = async () => {
    setConfirmDialogOpen(false);
    setRecalculating(true);
    try {
      const response = await post("/admin/players/recalculate-ownership");
      const data = response?.data ?? response;
      setSnackbar({
        open: true,
        message: data?.message || "Ownership recalculated successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to recalculate ownership:", error);
      setSnackbar({
        open: true,
        message: "Failed to recalculate ownership",
        severity: "error",
      });
    } finally {
      setRecalculating(false);
    }
  };

  // Fetch leagues and teams separately from the database
  useEffect(() => {
    // Fetch active tournament to default the filter. The id guard keeps a
    // stubbed/unexpected response from leaking a non-uuid into the league
    // query param, which the backend rejects with a 400.
    get("/tournament-calendars/active")
      .then((response) => {
        const data = response?.data ?? response;
        if (typeof data?.id === "string" && data.id) {
          setSelectedLeague(data.id);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch active tournament:", error);
      });

    // Fetch leagues
    get("/admin/players/leagues")
      .then((response) => {
        const data = response?.data ?? response;
        setLeagues(data || []);
      })
      .catch((error) => {
        console.error("Failed to fetch leagues:", error);
        setLeagues([]);
      });

    // Fetch teams
    get("/admin/players/teams")
      .then((response) => {
        const data = response?.data ?? response;
        setTeams(data || []);
      })
      .catch((error) => {
        console.error("Failed to fetch teams:", error);
        setTeams([]);
      });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(PAGE_SIZE),
    });
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (selectedPosition) params.set("position", selectedPosition);
    if (selectedLeague) params.set("league", selectedLeague);
    if (selectedTeam) params.set("contestantId", selectedTeam);
    if (selectedStatuses.length > 0) {
      params.set("availabilityDisplay", selectedStatuses.join(','));
    }

    get(`/admin/players/?${params.toString()}`)
      .then((response) => {
        const items = response?.data?.items ?? response?.items ?? [];
        const pagination = response?.data?.pagination;
        const mapped = items.map((p) => ({
          id: p.id,
          firstName: p.firstName,
          lastName: p.lastName,
          matchName: p.matchName,
          name: `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim(),
          team: p.teamName ?? "-",
          position: p.position,
          league: p.tournamentName ?? "Unknown",
          availabilityDisplay: p.availabilityDisplay || "available",
          price: p.price,
          totalPoints: p.totalPoints,
          percentSelected: p.percentSelected ?? 0,
          shirtKey: p.shirtKey,
          contestantFlagKey: p.contestantFlagKey,
          flagUrl: getFlagUrl(p.contestantFlagKey),
          jerseyUrl: getJerseyUrl(p.shirtKey),
          dreamTeamName: p.dreamTeamName ?? null,
          dreamTeamPrice: p.dreamTeamPrice ?? null,
          optaPersonId: p.optaPersonId ?? null,
          isVisible: p.isVisible ?? true,
          hasLeft: p.hasLeft ?? false,
        }));
        setPlayers(mapped);
        if (pagination) setTotalPages(pagination.totalPages);
      })
      .catch((error) => {
        console.error("Failed to fetch players:", error);
        setPlayers([]);
      });
  }, [page, debouncedSearch, selectedPosition, selectedLeague, selectedTeam, selectedStatuses]);

  return (
    <Container maxWidth="xl">
      <Breadcrumbs sx={{ mb: 2 }}>
        <Typography color="text.primary">Players</Typography>
      </Breadcrumbs>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Players</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            disabled={recalculating}
            onClick={() => setConfirmDialogOpen(true)}
          >
            {recalculating ? "Recalculating..." : "Recalculate Ownership"}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            Add Player
          </Button>
        </Box>
      </Box>

      <PlayersFilters
        inputStyles={inputStyles}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedLeague={selectedLeague}
        setSelectedLeague={setSelectedLeague}
        selectedTeam={selectedTeam}
        setSelectedTeam={setSelectedTeam}
        selectedPosition={selectedPosition}
        setSelectedPosition={handlePositionChange}
        leagues={leagues}
        teams={teams}
      />

      <PlayersTable 
        players={players} 
        onMenuOpen={handleMenuOpen} 
        onSortByName={handleSortByName}
        onSortByStatus={handleSortByStatus}
        onFilterByStatus={handleFilterByStatus}
        selectedStatuses={selectedStatuses}
        sortField={sortField}
        sortDirection={sortDirection}
      />

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3} mb={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEdit(menuPlayerId)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEditPlayer}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Price & Settings</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDelete(menuPlayerId)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <EditPlayerModal
        open={editModalOpen}
        player={selectedPlayer}
        onClose={handleModalClose}
        onSave={handlePlayerUpdated}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Recalculate Ownership</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will recalculate ownership percentages for all fantasy players based on current team rosters. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRecalculateOwnership} variant="contained" color="primary">
            Recalculate
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PlayersPage;
